import { useState, useEffect, useMemo } from "react";
import { useNitroStore } from "../store/Store";
import { IFavoriteOrigin, IFavoriteOrigins } from "../types/IFavoriteOrigins";
import NitroService from "../utils/nitro.service";

// Global cache to prevent multiple simultaneous requests
const requestCache = new Map<string, Promise<IFavoriteOrigins[]>>();
const dataCache = new Map<
  string,
  { data: IFavoriteOrigins[]; timestamp: number }
>();
const CACHE_DURATION = 5000; // 5 seconds cache

export const useFavoriteOrigins = (user: string, product: string) => {
  const [fetchFavoriteOrigins, setFetchFavoriteOrigins] = useNitroStore(
    (state) => [state.fetchFavoriteOrigins, state.setFetchFavoriteOrigins],
  );

  const [allFavoriteOrigins, setAllFavoriteOrigins] = useState<
    IFavoriteOrigins[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  useEffect(() => {
    if (!user || !product) {
      setAllFavoriteOrigins([]);
      setIsLoading(false);
      setShowWarning(false);
      return;
    }

    const cacheKey = `${user}-${product}`;

    // Check if we have fresh cached data
    const cachedData = dataCache.get(cacheKey);

    if (
      cachedData &&
      Date.now() - cachedData.timestamp < CACHE_DURATION &&
      !fetchFavoriteOrigins
    ) {
      setAllFavoriteOrigins(cachedData.data);
      setIsLoading(false);
      setShowWarning(
        cachedData.data.filter((o) => o.product === product)[0]?.favorites
          ?.length === 0,
      );
      return;
    }

    const performFetch = async () => {
      // Check if there's already a request in progress for this key
      let requestPromise = requestCache.get(cacheKey);

      if (!requestPromise) {
        // Only set loading for the first caller
        setIsLoading(true);
        setShowWarning(false);

        // Create new request promise
        requestPromise = NitroService.getFavoriteOrigins(user);
        requestCache.set(cacheKey, requestPromise);

        try {
          const result = await requestPromise;

          // Cache the result
          dataCache.set(cacheKey, { data: result, timestamp: Date.now() });

          const originsByProduct =
            result.filter((o) => o.product === product)[0]?.favorites || [];
          if (originsByProduct && originsByProduct.length > 0) {
            setAllFavoriteOrigins(result);
            setShowWarning(false);
          } else {
            setAllFavoriteOrigins([]);
            setShowWarning(true);
          }
        } catch (error) {
          console.error("Failed to fetch favorite origins:", error);
          setAllFavoriteOrigins([]);
          setShowWarning(true);
        } finally {
          setIsLoading(false);
          // Clean up the request cache
          requestCache.delete(cacheKey);

          if (fetchFavoriteOrigins) {
            setFetchFavoriteOrigins(false);
          }
        }
      } else {
        // Request already in progress, wait for it
        try {
          const result = await requestPromise;
          const originsByProduct =
            result.filter((o) => o.product === product)[0]?.favorites || [];
          if (originsByProduct && originsByProduct.length > 0) {
            setAllFavoriteOrigins(result);
            setShowWarning(false);
          } else {
            setAllFavoriteOrigins([]);
            setShowWarning(true);
          }
        } catch (error) {
          console.error("Failed to fetch favorite origins:", error);
          setAllFavoriteOrigins([]);
          setShowWarning(true);
        }
      }
    };

    performFetch();
  }, [user, product, fetchFavoriteOrigins, setFetchFavoriteOrigins]);
  const sortByCreateDate = (a: any, b: any) => {
    const dateA = a.create_date;
    const dateB = b.create_date;

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    if (dateA.year !== dateB.year) return dateB.year - dateA.year;
    if (dateA.month !== dateB.month) return dateB.month - dateA.month;
    if (dateA.day !== dateB.day) return dateB.day - dateA.day;
    if (dateA.hour !== dateB.hour) return dateB.hour - dateA.hour;
    if (dateA.minute !== dateB.minute) return dateB.minute - dateA.minute;
    return dateB.second - dateA.second;
  };

  const { kochOrigins, competitorOrigins, kochPrices, competitorPrices } =
    useMemo(() => {
      const originsForProduct =
        allFavoriteOrigins.find(
          (o) => o.product.toUpperCase() === product.toUpperCase(),
        )?.favorites || [];

      const koch: IFavoriteOrigin[] = [];
      const competitor: IFavoriteOrigin[] = [];

      originsForProduct.forEach((origin) => {
        if (origin.competitor) {
          competitor.push(origin);
        } else {
          koch.push(origin);
        }
      });

      const getPrices = (origins: IFavoriteOrigin[]): number[] => {
        const prices: number[] = [];
        origins.forEach((origin) => {
          if (origin.truckMinPrice != null) prices.push(origin.truckMinPrice);
          if (origin.truckMaxPrice != null) prices.push(origin.truckMaxPrice);
          if (origin.railMinPrice != null) prices.push(origin.railMinPrice);
          if (origin.railMaxPrice != null) prices.push(origin.railMaxPrice);
          if (origin.bargeMinPrice != null) prices.push(origin.bargeMinPrice);
          if (origin.bargeMaxPrice != null) prices.push(origin.bargeMaxPrice);
        });
        return prices;
      };

      return {
        kochOrigins: koch.sort(sortByCreateDate),
        competitorOrigins: competitor.sort(sortByCreateDate),
        kochPrices: getPrices(koch),
        competitorPrices: getPrices(competitor),
      };
    }, [allFavoriteOrigins, product]);

  return {
    isLoading,
    showWarning,
    kochOrigins,
    competitorOrigins,
    kochPrices,
    competitorPrices,
  };
};
